/**
 * Wagmi Component - Simple Expert Refactor
 * 
 * Improvements:
 * - Memoized component for performance
 * - Better text processing with utilities
 * - Enhanced accessibility
 * - Cleaner prop handling
 * - Error boundaries for icon component
 */

import React, { memo, useMemo } from "react";

// Assets
import heart_icon from "@assets/images/heart_icon.png";
import WagmiIcon from "@assets/images/WagmiIcon.jsx";

// Context
import { useAppContext } from "@context/AppContext";

// Constants
const FUNDRAISIN_WEBSITE = `https://${import.meta.env.MODE === "development" ? 'test' : 'www'}.fundraisin.app`;
const DEFAULT_NFT_PLURAL = "NFTs";
const DEFAULT_PURPOSE = "raise funds for important causes";

// Simple Icon Component with Error Boundary (no memo - simple component)
const SafeWagmiIcon = ({ color, size = "default" }) => {
  try {
    return <WagmiIcon color={color} size={size} />;
  } catch (error) {
    console.warn("WagmiIcon failed to render:", error);
    return (
      <div
        className="wagmi-icon-fallback"
        style={{
          width: "40px",
          height: "40px",
          backgroundColor: color || "#333",
          borderRadius: "50%"
        }}
        aria-label="Wagmi icon"
      />
    );
  }
};

// Simple Title Component (no memo - simple JSX)
const WagmiTitle = ({ primaryColor }) => (
  <div className="wagmi__title-box" role="banner">
    <img
      className="wagmi__title-heart-icon"
      src={heart_icon}
      alt="Heart icon representing love and community"
      loading="lazy"
    />
    <h1 className="wagmi__title-text plus-sign" aria-hidden="true">+</h1>
    <SafeWagmiIcon color={primaryColor} />
    <h1 className="wagmi__title-text plus-sign" aria-hidden="true">+</h1>
    <h1 className="wagmi__title-text">U</h1>
  </div>
);

// Simple Description Component (no memo - props change infrequently)
const WagmiDescription = ({ nftPlural, purpose }) => {
  const descriptionText = useMemo(() => {
    return `The purpose of the ${nftPlural} collection is to ${purpose}. If you would like to sponsor your own Fund Raisin campaign, or if you are an artist, please visit`;
  }, [nftPlural, purpose]);

  return (
    <div className="wagmi__content">
      <p className="wagmi__desc">
        {descriptionText}{" "}
        <a 
          href={FUNDRAISIN_WEBSITE} 
          target="_blank" 
          rel="noopener noreferrer"
          aria-label="Visit Fund Raisin website to create your own campaign"
        >
          {FUNDRAISIN_WEBSITE}
        </a>{" "}
        to create your own.
      </p>
      <p className="wagmi__desc wagmi__tagline">
        <strong>Fund Raisin, for any reason!</strong>
      </p>
    </div>
  );
};// Main Component
const Wagmi = memo(() => {
  const { themeConfig } = useAppContext();

  // Memoized values with fallbacks
  const componentData = useMemo(() => ({
    primaryColor: themeConfig?.PRIMARY || "#333333",
    nftPlural: themeConfig?.APP_NFT_PLURAL || DEFAULT_NFT_PLURAL,
    purpose: themeConfig?.APP_PURPOSE || DEFAULT_PURPOSE
  }), [themeConfig]);

  return (
    <section
      id="wagmi"
      className="default-settings"
      aria-labelledby="wagmi-title"
    >
      <div className="sr-only">
        <h2 id="wagmi-title">Community and Purpose</h2>
      </div>

      <WagmiTitle primaryColor={componentData.primaryColor} />

      <WagmiDescription
        nftPlural={componentData.nftPlural}
        purpose={componentData.purpose}
      />
    </section>
  );
});

Wagmi.displayName = 'Wagmi';

export default Wagmi;
